import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Projects() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleCount = windowWidth < 768 ? 1 : windowWidth < 1024 ? 2 : 4;

  const projects = [
    {
      title: t('projects.store.title'),
      description: t('projects.store.desc'),
      icon: 'ri-store-2-line',
      image: '/alebelstore1.png',
      tags: [t('tag.luxury'), t('tag.ecommerce'), t('tag.retail')],
      website: 'https://www.alebelstore.com',
    },
    {
      title: t('projects.tech.title'),
      description: t('projects.tech.desc'),
      icon: 'ri-code-s-slash-line',
      image: '/alebeltech.jpg',
      tags: [t('tag.technology'), t('tag.digital'), t('tag.software')],
      website: 'https://www.alebeltech.com',
    },
    {
      title: t('projects.energy.title'),
      description: t('projects.energy.desc'),
      icon: 'ri-lightbulb-line',
      image: '/alebelenergy.webp',
      tags: [t('tag.energy'), t('tag.batterySystems'), t('tag.industrial')],
      website: 'https://www.alebelenergy.com',
    },
    {
      title: t('projects.healthcare.title'),
      description: t('projects.healthcare.desc'),
      icon: 'ri-heart-pulse-line',
      image: '/healthcare.png',
      tags: [t('tag.healthcare'), t('tag.pharmaceuticals'), t('tag.wellness')],
      website: 'https://www.alebelhealthcare.com',
    },
    {
      title: t('projects.racing.title'),
      description: t('projects.racing.desc'),
      icon: 'ri-steering-2-line',
      image: '/alebelracing.png',
      tags: [t('tag.motorsport'), t('tag.performance'), t('tag.racing')],
      website: 'https://www.alebelracing.com',
    },
    {
      title: t('projects.electronics.title'),
      description: t('projects.electronics.desc'),
      icon: 'ri-cpu-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20electronics%20circuit%20board%20with%20IoT%20devices%20and%20embedded%20systems%2C%20RF%20communication%20modules%2C%20smart%20control%20systems%20technology%2C%20professional%20tech%20photography%20with%20warm%20lighting%2C%20innovation%20in%20electronics%20industry%2C%20premium%20quality%20components&width=800&height=500&seq=alebel-electronics-003&orientation=landscape',
      tags: [t('tag.electronics'), t('tag.iot'), t('tag.smartSystems')],
      website: '/portfolio/electronics',
    },
    {
      title: t('projects.device.title'),
      description: t('projects.device.desc'),
      icon: 'ri-computer-line',
      image: '/alebeldevice.jpg',
      tags: [t('tag.devices'), t('tag.smartTech'), t('tag.electronics')],
      website: '/portfolio/device',
    },
    {
      title: t('projects.rnd.title'),
      description: t('projects.rnd.desc'),
      icon: 'ri-flask-line',
      image: 'alebelrnd.jpeg',
      tags: [t('tag.research'), t('tag.innovation'), t('tag.technologyDev')],
      website: '/portfolio/rnd',
    },
    {
      title: t('projects.construction.title'),
      description: t('projects.construction.desc'),
      icon: 'ri-building-2-line',
      image: '/alebelconstruction.jpg',
      tags: [t('tag.construction'), t('tag.infrastructure'), t('tag.development')],
      website: '/portfolio/construction',
    },
  ];

  const maxIndex = projects.length - visibleCount;

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  return (
    <section id="projects" className="py-32 bg-navy relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.2) 0%, transparent 50%)`,
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-px bg-gold"></div>
            <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('projects.label')}</span>
            <div className="w-12 h-px bg-gold"></div>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
            {t('projects.title1')} <span className="text-gold">{t('projects.title2')}</span>
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t('projects.description')}
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: visibleCount === 1
                  ? `translateX(${isRTL ? '' : '-'}${currentIndex * 100}%)`
                  : isRTL
                    ? `translateX(calc(${currentIndex * 100 / visibleCount}% + ${currentIndex * 2 / visibleCount}rem))`
                    : `translateX(calc(-${currentIndex * 100 / visibleCount}% - ${currentIndex * 2 / visibleCount}rem))`,
                gap: visibleCount === 1 ? '0px' : '2rem',
              }}
            >
              {projects.map((project, index) => (
                <div
                  key={index}
                  className="group flex-shrink-0 px-1"
                  style={{
                    width: visibleCount === 1
                      ? '100%'
                      : `calc(${100 / visibleCount}% - ${2 * (visibleCount - 1) / visibleCount}rem)`,
                  }}
                >
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/10 hover:border-gold/30 transition-all duration-500 h-full flex flex-col">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent"></div>
                      <div className={`absolute top-6 ${isRTL ? 'left-6' : 'right-6'} w-14 h-14 bg-gradient-to-br from-gold to-gold-dark rounded-2xl flex items-center justify-center shadow-lg`}>
                        <i className={`${project.icon} text-2xl text-navy`}></i>
                      </div>
                    </div>

                    <div className="p-6 md:p-8 flex-1 flex flex-col">
                      <h3 className="text-xl md:text-2xl font-serif text-white mb-4 group-hover:text-gold transition-colors duration-300">
                        {project.title}
                      </h3>
                      <p className="text-white/70 leading-relaxed mb-6 flex-1 text-sm">
                        {project.description}
                      </p>
                      <div className={`flex flex-wrap gap-2 mb-6 ${isRTL ? 'justify-end' : ''}`}>
                        {project.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full border border-gold/20">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Link
                          to="/portfolio"
                          className={`inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors duration-300 cursor-pointer text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          {t('projects.viewDetails')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-up-line`}></i>
                        </Link>
                        <a
                          href={project.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 text-gold/80 hover:text-gold transition-colors duration-300 cursor-pointer text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          {t('projects.websiteLabel')} <i className="ri-external-link-line"></i>
                        </a>
                      </div>
                    </div>

                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1/2 bg-gold/10 blur-3xl"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:border-gold hover:text-navy transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line text-xl`}></i>
            </button>
            <div className="flex gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${currentIndex === i ? 'bg-gold w-6' : 'bg-white/30 w-2'}`}
                />
              ))}
            </div>
            <button
              onClick={handleNext}
              disabled={currentIndex === maxIndex}
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:border-gold hover:text-navy transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        <div className="mt-20 bg-gradient-to-r from-gold/10 to-gold/5 rounded-3xl p-8 md:p-10 border border-gold/20">
          <div className={`flex flex-col lg:flex-row items-center justify-between gap-8 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className={`text-center ${isRTL ? 'lg:text-right' : 'lg:text-start'}`}>
              <h3 className="text-2xl font-serif text-white mb-3">{t('projects.investmentFocus')}</h3>
              <p className="text-white/70 max-w-xl">{t('projects.investmentFocusDesc')}</p>
            </div>
            <Link
              to="/portfolio"
              className={`px-8 py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {t('projects.viewAll')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}